import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/hooks';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, CheckCircle2, Circle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ChecklistItem {
  key: string;
  title: string;
  description: string;
  action: string;
  href: string;
  completed: boolean;
}

export function GettingStartedChecklist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadChecklist();
      loadDismissalState();
    }
  }, [user?.id]);

  const loadChecklist = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('getting_started_checklist')
        .eq('id', user!.id)
        .single();

      if (data?.getting_started_checklist) {
        const checklist = data.getting_started_checklist as Record<string, any>;
        const items: ChecklistItem[] = [
          {
            key: 'created_organization',
            title: 'Create your organization',
            description: 'Set up your company profile',
            action: 'Create Organization',
            href: '/onboarding',
            completed: checklist.created_organization || false
          },
          {
            key: 'invited_team_member',
            title: 'Invite team members',
            description: 'Add co-founders, employees, and advisors',
            action: 'Invite Team',
            href: '/users',
            completed: checklist.invited_team_member || false
          },
          {
            key: 'created_project',
            title: 'Create a project',
            description: 'Set up your first equity pool',
            action: 'New Project',
            href: '/dashboard',
            completed: checklist.created_project || false
          },
          {
            key: 'added_equity_grant',
            title: 'Add equity grants',
            description: 'Allocate shares to team members',
            action: 'Add Grant',
            href: '/cap-table',
            completed: checklist.added_equity_grant || false
          },
          {
            key: 'ran_simulation',
            title: 'Run a simulation',
            description: 'Model funding rounds and exit scenarios',
            action: 'Run Simulation',
            href: '/simulations',
            completed: checklist.ran_simulation || false
          },
          {
            key: 'generated_document',
            title: 'Generate a document',
            description: 'Create equity agreements and letters',
            action: 'Generate Document',
            href: '/documents',
            completed: checklist.generated_document || false
          },
          {
            key: 'exported_cap_table',
            title: 'Export your cap table',
            description: 'Download reports for investors or advisors',
            action: 'Export Data',
            href: '/cap-table',
            completed: checklist.exported_cap_table || false
          }
        ];
        setChecklist(items);
      }
    } catch (error) {
      console.error('Error loading checklist:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDismissalState = async () => {
    try {
      const { data } = await supabase
        .from('user_settings')
        .select('checklist_progress')
        .eq('user_id', user!.id)
        .single();

      if (data?.checklist_progress && (data.checklist_progress as Record<string, any>).dismissed) {
        setIsDismissed(true);
      }
    } catch (error) {
      console.error('Error loading dismissal state:', error);
    }
  };

  const handleDismiss = async () => {
    try {
      await supabase
        .from('user_settings')
        .upsert({
          user_id: user!.id,
          checklist_progress: { dismissed: true },
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });
      setIsDismissed(true);
    } catch (error) {
      console.error('Error dismissing checklist:', error);
    }
  };

  const handleAction = (href: string) => {
    navigate(href);
  };

  if (isLoading || isDismissed || checklist.length === 0) {
    return null;
  }

  const completedCount = checklist.filter(item => item.completed).length;
  const progress = (completedCount / checklist.length) * 100;
  const allCompleted = completedCount === checklist.length;

  return (
    <Card className={allCompleted ? 'border-green-500/50' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              Getting Started
              {allCompleted && (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              )}
            </CardTitle>
            <CardDescription>
              {allCompleted
                ? "Congratulations! You've completed all setup tasks."
                : `${completedCount} of ${checklist.length} tasks completed`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {!allCompleted && (
          <Progress value={progress} className="mt-3" />
        )}
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <div className="space-y-3">
            {checklist.map((item) => (
              <div
                key={item.key}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="pt-0.5">
                  {item.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 space-y-1">
                  <h4 className={`font-medium text-sm ${
                    item.completed ? 'text-muted-foreground line-through' : ''
                  }`}>
                    {item.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {item.description}
                  </p>
                </div>
                {!item.completed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAction(item.href)}
                  >
                    {item.action}
                  </Button>
                )}
              </div>
            ))}
          </div>
          
          {allCompleted && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">
                🎉 Great job! You've completed all the getting started tasks. 
                You're ready to manage your equity like a pro!
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}